import { forwardRef } from 'react';

import { styled } from '@spazious/dollhouse-theme';

import { BaseExtendProps, createBase } from '../Base/index.js';
import { Typography } from '../Typography';

export type CheckboxWrapperElement = HTMLLabelElement;
export interface CheckboxWrapperProps
  extends BaseExtendProps<React.LabelHTMLAttributes<CheckboxWrapperElement>> {}

const CheckboxWrapper = styled(createBase('label')).withConfig({
  displayName: 'CheckboxWrapper',
})<CheckboxWrapperProps>`
  z-index: 0;
  position: relative;
  display: inline-block;
  line-height: 1.5;
  margin-right: 10px;

  /* Input */
  & > input {
    appearance: none;
    -moz-appearance: none;
    -webkit-appearance: none;
    z-index: -1;
    position: absolute;
    left: -10px;
    top: -8px;
    display: block;
    margin: 0;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    background-color: var(--spz-sys-color-on-surface);
    box-shadow: none;
    outline: none;
    opacity: 0;
    transform: scale(1);
    pointer-events: none;
    transition: opacity 0.3s, transform 0.2s;
  }

  /* Span */
  & > span {
    display: inline-block;
    width: 100%;
    cursor: pointer;
  }

  /* Box */
  & > span::before {
    content: '';
    display: inline-block;
    box-sizing: border-box;
    margin: 3px 11px 3px 1px;
    border: solid 2px; /* Safari */
    border-color: var(--spz-sys-color-on-surface);
    border-radius: 2px;
    width: 18px;
    height: 18px;
    vertical-align: middle;
    transition: border-color 0.2s, background-color 0.2s;
    transform: translate(0, -1px);
  }

  /* Checkmark */
  & > span::after {
    content: '';
    display: block;
    position: absolute;
    top: 3px;
    left: px;
    width: 17px;
    height: 9px;
    border: solid 2px transparent;
    border-right: none;
    border-top: none;
    transform: translate(3px, 4px) rotate(-45deg);
  }

  /* Checked, Indeterminate */
  & > input:checked,
  & > input:indeterminate {
    background-color: var(--spz-sys-color-primary);
  }

  & > input:checked + span::before,
  & > input:indeterminate + span::before {
    border-color: var(--spz-sys-color-primary);
    background-color: var(--spz-sys-color-primary);
  }

  & > input:checked + span::after,
  & > input:indeterminate + span::after {
    border-color: var(--spz-sys-color-on-primary);
  }

  & > input:indeterminate + span::after {
    border-left: none;
    transform: translate(4px, 3px);
  }

  /* Hover, Focus */
  &:hover > input {
    opacity: 0.04;
  }

  & > input:focus {
    opacity: 0.12;
  }

  &:hover > input:focus {
    opacity: 0.16;
  }

  /* Active */
  & > input:active {
    opacity: 1;
    transform: scale(0);
    transition: transform 0s, opacity 0s;
  }

  & > input:active + span::before {
    border-color: var(--spz-sys-color-on-primary);
  }

  & > input:checked:active + span::before {
    border-color: transparent;
    background-color: var(--spz-sys-color-on-surface);
  }

  /* Disabled */
  & > input:disabled {
    opacity: 0;
  }

  & > input:disabled + span {
    color: var(--spz-sys-color-on-surface);
    cursor: initial;
  }

  & > input:disabled + span::before {
    border-color: currentColor;
  }

  & > input:checked:disabled + span::before,
  & > input:indeterminate:disabled + span::before {
    border-color: transparent;
    background-color: currentColor;
  }
`;

export type CheckboxElement = HTMLInputElement;
export interface CheckboxProps extends BaseExtendProps<React.InputHTMLAttributes<CheckboxElement>> {
  label: string;
}

export const Checkbox = styled(
  forwardRef<HTMLInputElement, CheckboxProps>(({ label, ...props }, fowardedRef) => {
    return (
      <CheckboxWrapper>
        <input ref={fowardedRef} {...props} type="checkbox" />
        <Typography>{label}</Typography>
      </CheckboxWrapper>
    );
  })
).withConfig({
  displayName: 'Checkbox',
})``;
